// src/components/Layout.js
import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Tabs,
  Tab,
  Badge,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  CircularProgress,
  useMediaQuery,
  Tooltip
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Menu as MenuIcon,
  Notifications as NotificationsIcon,
  Public as PublicIcon,
  Group as GroupIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  Dashboard as DashboardIcon,
  School as SchoolIcon,
  School as ProfessionalIcon,
  Help as HelpIcon
} from '@mui/icons-material';
import { collection, getCountFromServer } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationsContext';
import { THEME_COLORS } from '../styles/theme';
import { fetchUserProfile } from '../features/collaboration/services/collaborationService';
import logo from '../assets/images/logo.png';
import NotificationsMenu from './Notifications/NotificationsMenu';

const Layout = () => {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [notificationsAnchorEl, setNotificationsAnchorEl] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const { totalBadgeCount } = useNotifications();
  const [userCount, setUserCount] = useState(0);

  useEffect(() => {
    const fetchUserCount = async () => {
      try {
        const snapshot = await getCountFromServer(collection(db, 'userProfiles'));
        setUserCount(snapshot.data().count);
      } catch (error) {
        console.error('Error fetching user count:', error);
      }
    };
    if (currentUser) fetchUserCount();
  }, [currentUser]);

  useEffect(() => {
    const loadUserProfile = async () => {
      if (!currentUser) {
        setLoading(false);
        return;
      }
      
      try {
        const profile = await fetchUserProfile(currentUser.uid);
        setUserProfile(profile);
      } catch (error) {
        console.error("Error loading user profile:", error);
      } finally {
        setLoading(false);
      }
    };
    
    loadUserProfile();
  }, [currentUser]);
  
  // Safe access to profile data
  const userName = userProfile?.name || (currentUser?.displayName || 'User');
  const userInitial = userName ? userName.charAt(0) : '?';
  const userImage = userProfile?.profileImage || null;
  const userEmail = currentUser?.email || '';
  
  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleNotificationsOpen = (event) => {
    setNotificationsAnchorEl(event.currentTarget);
  };
  
  const handleNotificationsClose = () => {
    setNotificationsAnchorEl(null);
  };
  
  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };
  
  const handleLogout = async () => {
    handleProfileMenuClose();
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error("Logout error:", error);
    }
  };
  
  const handleTabChange = (event, newValue) => {
    navigate(newValue);
  };
  
  const toggleDrawer = () => {
    setDrawerOpen(!drawerOpen);
  };
  
  const currentTab = location.pathname.split('/')[1] || 'collaboration';
  
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <AppBar position="static" sx={{ bgcolor: '#f6f5f5', color: THEME_COLORS.primary }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            aria-label="menu"
            onClick={toggleDrawer}
            sx={{ mr: 0 }}
          >
            <MenuIcon />
          </IconButton>
          
          <Box sx={{ display: 'flex', alignItems: 'center', mr: { xs: 2, sm: 4 }, minWidth: 0 }}>
            <img
              src={logo}
              alt="Teachmate Logo"
              style={{
                height: isMobile ? 64 : 36,
                width: 'auto',
                maxWidth: '100%',
                objectFit: 'contain'
              }}
            />
          </Box>
          
          <Tabs 
            value={currentTab} 
            onChange={handleTabChange}
            textColor="inherit"
            sx={{ 
              flexGrow: 1,
              '& .MuiTab-root': { 
                textTransform: 'none',
                fontWeight: 500,
                minWidth: { xs: 56, sm: 120 },
                px: { xs: 1, sm: 2 }
              },
              '& .MuiTab-wrapper': {
                flexDirection: { xs: 'column', sm: 'row' },
                gap: { xs: 0.25, sm: 0.75 }
              }
            }}
          >
            
            <Tab
              label={isMobile ? '' : 'Collaboration'}
              value="collaboration"
              icon={
                <Tooltip
                  title={`${userCount} educator${userCount !== 1 ? 's' : ''} on the platform`}
                  arrow
                  placement="top"
                >
                  <Badge
                    badgeContent={userCount}
                    color="primary"
                    max={9999}
                    sx={{
                      '& .MuiBadge-badge': {
                        fontSize: '0.65rem',
                        height: 18,
                        minWidth: 18,
                        top: -2,
                        right: -4
                      }
                    }}
                  >
                    <GroupIcon />
                  </Badge>
                </Tooltip>
              }
              iconPosition={isMobile ? 'top' : 'start'}
            />

            <Tab 
              label={isMobile ? '' : 'Resources'} 
              value="resources" 
              icon={<ListItemIcon />} 
              iconPosition={isMobile ? 'top' : 'start'} 
            />

            <Tab 
              label={isMobile ? '' : 'Events'} 
              value="events" 
              icon={<PublicIcon />} 
              iconPosition={isMobile ? 'top' : 'start'} 
            />
            
            <Tab 
              label="Operational" 
              value="operational" 
              icon={<DashboardIcon />} 
              iconPosition="start" 
            />
            {/*
          <Tab 
              label="Tuition" 
              value="tuition" 
              icon={<SchoolIcon />} 
              iconPosition="start" 
            />
          <Tab 
              label="Professional" 
              value="professional" 
              icon={<ProfessionalIcon />} 
              iconPosition="start" 
            />
          */}
          </Tabs>
         
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton color="inherit" onClick={handleNotificationsOpen}>
              <Badge badgeContent={totalBadgeCount} color="error">
                <NotificationsIcon />
              </Badge>
            </IconButton>
            
            {loading ? (
              <Box sx={{ ml: 1, display: 'flex', alignItems: 'center' }}>
                <CircularProgress size={24} sx={{ color: 'white' }} />
              </Box>
            ) : (
              <IconButton
                onClick={handleProfileMenuOpen}
                color="inherit"
                sx={{ ml: 1 }}
              >
                <Avatar 
                  src={userImage} 
                  alt={userName}
                  sx={{ width: 32, height: 32 }}
                >
                  {userInitial}
                </Avatar>
              </IconButton>
            )}
          </Box>
        </Toolbar>
      </AppBar>
      
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="subtitle1" fontWeight="bold">
            {userName}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {userEmail}
          </Typography>
        </Box>
        <Divider />
        <MenuItem onClick={() => { handleProfileMenuClose(); navigate('/profile'); }}>
          <ListItemIcon>
            <Avatar sx={{ width: 24, height: 24 }}>
              {userInitial}
            </Avatar>
          </ListItemIcon>
          <ListItemText>Profile</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { handleProfileMenuClose(); navigate('/settings'); }}>
          <ListItemIcon>
            <SettingsIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Settings</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Logout</ListItemText>
        </MenuItem>
      </Menu>

      <NotificationsMenu
        anchorEl={notificationsAnchorEl}
        onClose={handleNotificationsClose}
      />
      
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={toggleDrawer}
      >
        <Box
          sx={{ width: 250 }}
          role="presentation"
          onClick={toggleDrawer}
        >
          <Box sx={{ p: 2 }}>
            <img src={logo} alt="Teachmate Logo" style={{ height: 36, marginRight: 10 }} />
          </Box>
          
          <Divider />
          <List>
            <ListItem button onClick={() => navigate('/events')}>
              <ListItemIcon>
                <PublicIcon sx={{ color: currentTab === 'events' ? THEME_COLORS.primary : 'inherit' }} />
              </ListItemIcon>
              <ListItemText 
                primary="Events" 
                sx={{ 
                  color: currentTab === 'events' ? THEME_COLORS.primary : 'inherit',
                  '& .MuiTypography-root': { 
                    fontWeight: currentTab === 'events' ? 'bold' : 'normal' 
                  }
                }} 
              />
            </ListItem>
            <ListItem button onClick={() => navigate('/collaboration')}>
              <ListItemIcon>
                <GroupIcon sx={{ color: currentTab === 'collaboration' ? THEME_COLORS.primary : 'inherit' }} />
              </ListItemIcon>
              <ListItemText 
                primary="Collaboration" 
                sx={{ 
                  color: currentTab === 'collaboration' ? THEME_COLORS.primary : 'inherit',
                  '& .MuiTypography-root': { 
                    fontWeight: currentTab === 'collaboration' ? 'bold' : 'normal' 
                  }
                }} 
              />
            </ListItem>
            <ListItem button onClick={() => navigate('/operational')}>
              <ListItemIcon>
                <DashboardIcon sx={{ color: currentTab === 'operational' ? THEME_COLORS.primary : 'inherit' }} />
              </ListItemIcon>
              <ListItemText 
                primary="Operational" 
                sx={{ 
                  color: currentTab === 'operational' ? THEME_COLORS.primary : 'inherit',
                  '& .MuiTypography-root': { 
                    fontWeight: currentTab === 'operational' ? 'bold' : 'normal' 
                  }
                }} 
              />
            </ListItem>
            <ListItem button onClick={() => navigate('/tuition')}>
              <ListItemIcon>
                <SchoolIcon sx={{ color: currentTab === 'tuition' ? THEME_COLORS.primary : 'inherit' }} />
              </ListItemIcon>
              <ListItemText 
                primary="Tuition" 
                sx={{ 
                  color: currentTab === 'tuition' ? THEME_COLORS.primary : 'inherit',
                  '& .MuiTypography-root': { 
                    fontWeight: currentTab === 'tuition' ? 'bold' : 'normal' 
                  }
                }} 
              />
            </ListItem>
            <ListItem button onClick={() => navigate('/professional')}>
              <ListItemIcon>
                <ProfessionalIcon sx={{ color: currentTab === 'professional' ? THEME_COLORS.primary : 'inherit' }} />
              </ListItemIcon>
              <ListItemText 
                primary="Professional" 
                sx={{ 
                  color: currentTab === 'professional' ? THEME_COLORS.primary : 'inherit',
                  '& .MuiTypography-root': { 
                    fontWeight: currentTab === 'professional' ? 'bold' : 'normal' 
                  }
                }} 
              />
            </ListItem>
          </List>
          <Divider />
          <List>
            <ListItem component="div" sx={{ cursor: 'pointer' }}>
              <ListItemIcon>
                <HelpIcon />
              </ListItemIcon>
              <ListItemText primary="Help & Support" />
            </ListItem>
          </List>
        </Box>
      </Drawer>
      
      <Box component="main" sx={{ flexGrow: 1, bgcolor: '#f5f7fb', overflow: 'auto' }}>
        <Outlet />
      </Box>
    </Box>
  );
};

export default Layout;
